import axios from 'axios';
import { supabase } from '../lib/supabase';
import { track as trackBreadcrumb } from '../lib/breadcrumbs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

// Git short SHA stamped at build time by craco.config.js. Sent on every API
// request as X-App-Version so server logs can be correlated with a specific
// build, and embedded in every bug report.
export const APP_VERSION = process.env.REACT_APP_GIT_SHA || 'dev';

// Get the current Supabase access token. If the cached session is missing
// or expired, force a refresh so we don't silently send unauthenticated
// requests to protected endpoints (which surface as a confusing 403/500
// with body "Not authenticated" instead of a clean re-login).
const getToken = async () => {
  let { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    const refreshed = await supabase.auth.refreshSession();
    session = refreshed?.data?.session ?? null;
  }
  return session?.access_token ?? null;
};

// Get current org ID from localStorage
const getOrgId = () => localStorage.getItem('currentOrgId');

// Axios instance — token injected per-request
const api = axios.create({
  baseURL: API_URL,
  timeout: 120000,
});

api.interceptors.request.use(async (config) => {
  const token = await getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  } else {
    // No session — fail fast with a clear error rather than letting the
    // request hit the backend without an Authorization header (which would
    // come back as 403 "Not authenticated" and look like a server bug).
    const err = new Error('Session expirée — veuillez vous reconnecter.');
    err.code = 'NO_SESSION';
    throw err;
  }
  config.headers['X-App-Version'] = APP_VERSION;
  return config;
});

// On 401 from any protected call, the token we sent was rejected (expired
// mid-flight or revoked). Sign out cleanly and bounce to /login.
// On any other error, push an `api_error` breadcrumb so it lands in bug
// reports (with the X-Request-ID server header for backend log correlation).
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error?.response?.status === 401) {
      try { await supabase.auth.signOut(); } catch { /* ignore */ }
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.assign('/login?reason=session_expired');
      }
    } else if (error?.response) {
      // Non-401 failure — record it for the bug report telemetry.
      try {
        const cfg = error.config || {};
        const resp = error.response;
        let bodyStr = '';
        try {
          bodyStr = typeof resp.data === 'string'
            ? resp.data
            : JSON.stringify(resp.data);
        } catch { bodyStr = String(resp.data); }
        trackBreadcrumb('api_error', {
          url: cfg.url || '',
          method: (cfg.method || 'get').toUpperCase(),
          status: resp.status,
          requestId: resp.headers?.['x-request-id'] || null,
          body: bodyStr.slice(0, 500),
        });
      } catch { /* never break the error pipeline */ }
    } else if (error?.request) {
      // Request never made it / no response — also worth recording.
      try {
        const cfg = error.config || {};
        trackBreadcrumb('api_error', {
          url: cfg.url || '',
          method: (cfg.method || 'get').toUpperCase(),
          status: 0,
          requestId: null,
          body: String(error.message || '').slice(0, 500),
        });
      } catch { /* noop */ }
    }
    return Promise.reject(error);
  },
);

// Organizations
export const orgsAPI = {
  listMine: async () => {
    // Fetch all orgs the current user belongs to via their memberships
    const token = await getToken();
    const response = await axios.get(`${API_URL}/orgs/mine`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  create: async ({ name, slug }) => {
    const token = await getToken();
    const response = await axios.post(`${API_URL}/orgs`, { name, slug }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  getLogo: async (orgId) => {
    const response = await api.get(`/orgs/${orgId}/logo`);
    return response.data; // { logo_url: string|null }
  },

  uploadLogo: async (orgId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const token = await getToken();
    const response = await axios.post(`${API_URL}/orgs/${orgId}/logo`, formData, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data; // { logo_url: string }
  },

  deleteLogo: async (orgId) => {
    const response = await api.delete(`/orgs/${orgId}/logo`);
    return response.data;
  },
};

// Profile API — current user's professional profile
export const profileAPI = {
  get: async () => {
    const response = await api.get('/me/profile');
    return response.data;
  },
  update: async ({ full_name, phone, job_title, years_of_experience }) => {
    const response = await api.put('/me/profile', { full_name, phone, job_title, years_of_experience });
    return response.data;
  },
};

// Dossiers API — all calls are org-scoped
export const dossiersAPI = {
  getAll: async () => {
    const orgId = getOrgId();
    const response = await api.get(`/orgs/${orgId}/dossiers`);
    return response.data;
  },

  getOne: async (id) => {
    const orgId = getOrgId();
    const response = await api.get(`/orgs/${orgId}/dossiers/${id}`);
    return response.data;
  },

  create: async (dossierData) => {
    const orgId = getOrgId();
    const response = await api.post(`/orgs/${orgId}/dossiers`, dossierData);
    return response.data;
  },

  update: async (id, dossierData) => {
    const orgId = getOrgId();
    const response = await api.put(`/orgs/${orgId}/dossiers/${id}`, dossierData);
    return response.data;
  },

  delete: async (id) => {
    const orgId = getOrgId();
    const response = await api.delete(`/orgs/${orgId}/dossiers/${id}`);
    return response.data;
  },

  downloadPDF: async (id, consultantName) => {
    const orgId = getOrgId();
    const response = await api.get(`/orgs/${orgId}/dossiers/${id}/pdf`, {
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `dossier_${consultantName.replace(/ /g, '_')}.pdf`);
    document.body.appendChild(link);
    link.click();
    setTimeout(() => { link.remove(); window.URL.revokeObjectURL(url); }, 100);
    return { success: true };
  },

  saveEditorContent: async (id, { editor_content, section_order, template_id, color_override, cover_overrides }) => {
    const orgId = getOrgId();
    const response = await api.put(`/orgs/${orgId}/dossiers/${id}/editor-content`, {
      editor_content,
      section_order,
      template_id,
      color_override,
      cover_overrides,
    });
    return response.data;
  },
};

// Email outreach API — connect mailbox, send client emails, send journal
export const emailAPI = {
  // Current user's connected mailboxes (no tokens)
  listConnections: async () => {
    const orgId = getOrgId();
    const { data } = await api.get(`/orgs/${orgId}/mailbox/connections`);
    return data;
  },
  // Returns { auth_url } — the caller redirects the browser to it
  connectGoogle: async () => {
    const orgId = getOrgId();
    const { data } = await api.get(`/orgs/${orgId}/mailbox/google/connect`);
    return data;
  },
  disconnect: async (connectionId) => {
    const orgId = getOrgId();
    await api.delete(`/orgs/${orgId}/mailbox/connections/${connectionId}`);
    return { success: true };
  },
  // payload: { to_emails, cc_emails?, subject, body_html, dossier_ids?, tender_id?, client_name? }
  send: async (payload) => {
    const orgId = getOrgId();
    const { data } = await api.post(`/orgs/${orgId}/emails/send`, payload);
    return data;
  },
  list: async () => {
    const orgId = getOrgId();
    const { data } = await api.get(`/orgs/${orgId}/emails`);
    return data;
  },
};

// Copilot BM — conversational agent. One turn per call; the caller replays the
// transcript so the copilot can resolve references like "ces 5 candidats".
export const copilotAPI = {
  // { enabled, beta, mailbox_connected } — lets the UI hide the entry point
  // rather than offering a button that fails.
  status: async () => {
    const orgId = getOrgId();
    const { data } = await api.get(`/orgs/${orgId}/copilot/status`);
    return data;
  },
  // messages: [{ role: 'user' | 'assistant', content }]
  // returns { reply, cards, steps }
  chat: async (messages) => {
    const orgId = getOrgId();
    const { data } = await api.post(`/orgs/${orgId}/copilot/chat`, { messages });
    return data;
  },
};

// CV Upload API
export const cvAPI = {
  // Upload CV and enqueue parse job — returns { job_id, status, async }
  upload: async (file) => {
    const orgId = getOrgId();
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/orgs/${orgId}/cv/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 30000, // just the upload, not the parse
    });
    return response.data; // { job_id, status, async, result? }
  },

  // Poll job status
  getJob: async (jobId) => {
    const orgId = getOrgId();
    const response = await api.get(`/orgs/${orgId}/cv/jobs/${jobId}`);
    return response.data; // { id, status, result_payload, error_message, ... }
  },
};

// Billing API
export const billingAPI = {
  getSummary: async () => {
    const orgId = getOrgId();
    const response = await api.get(`/orgs/${orgId}/billing/summary`);
    return response.data;
  },

  getPlans: async () => {
    const orgId = getOrgId();
    const response = await api.get(`/orgs/${orgId}/billing/plans`);
    return response.data;
  },

  createCheckout: async (planCode) => {
    const orgId = getOrgId();
    const response = await api.post(`/orgs/${orgId}/billing/checkout`, { plan_code: planCode });
    return response.data; // { checkout_url }
  },

  openPortal: async () => {
    const orgId = getOrgId();
    const response = await api.post(`/orgs/${orgId}/billing/portal`);
    return response.data; // { portal_url }
  },

  getUsage: async () => {
    const orgId = getOrgId();
    const response = await api.get(`/orgs/${orgId}/usage`);
    return response.data;
  },
};

// Members API
export const membersAPI = {
  list: async () => {
    const orgId = getOrgId();
    const response = await api.get(`/orgs/${orgId}/members`);
    return response.data;
  },

  invite: async ({ email, role }) => {
    const orgId = getOrgId();
    const response = await api.post(`/orgs/${orgId}/members/invite`, { email, role });
    return response.data; // { message, invite_link, email_sent, expires_at }
  },

  updateRole: async (memberId, role) => {
    const orgId = getOrgId();
    const response = await api.put(`/orgs/${orgId}/members/${memberId}/role`, { role });
    return response.data;
  },

  updateStatus: async (memberId, status) => {
    const orgId = getOrgId();
    const response = await api.put(`/orgs/${orgId}/members/${memberId}/status`, { status });
    return response.data;
  },

  remove: async (memberId) => {
    const orgId = getOrgId();
    await api.delete(`/orgs/${orgId}/members/${memberId}`);
  },
};

// Invitations API (accept flow — no org context needed)
export const invitationsAPI = {
  accept: async (token) => {
    const response = await api.post('/invitations/accept', { token });
    return response.data; // { message, organization_id, organization_name, role }
  },
};

// Platform Admin API
export const adminAPI = {
  getProfile: async () => {
    const response = await api.get('/me/profile');
    return response.data; // { id, full_name, avatar_url, is_platform_admin }
  },

  listOrganizations: async (search) => {
    const params = search ? { search } : {};
    const response = await api.get('/admin/organizations', { params });
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/admin/stats');
    return response.data;
  },

  updateOrgPlan: async (orgId, planCode) => {
    const response = await api.put(`/admin/organizations/${orgId}/plan`, { plan_code: planCode });
    return response.data;
  },

  getOrgDetail: async (orgId) => {
    const response = await api.get(`/admin/organizations/${orgId}`);
    return response.data; // { org, subscription, members, audit_logs }
  },

  createOrg: async (payload) => {
    // payload: { name, slug, plan_code, owner_user_id? }
    const response = await api.post('/admin/organizations', payload);
    return response.data;
  },

  deleteOrg: async (orgId) => {
    await api.delete(`/admin/organizations/${orgId}`);
  },

  addMember: async (orgId, payload) => {
    // payload: { user_id, role }
    const response = await api.post(`/admin/organizations/${orgId}/members`, payload);
    return response.data;
  },

  removeMember: async (orgId, memberId) => {
    await api.delete(`/admin/organizations/${orgId}/members/${memberId}`);
  },

  moveMember: async (orgId, memberId, payload) => {
    // payload: { target_org_id, role }
    const response = await api.put(`/admin/organizations/${orgId}/members/${memberId}/move`, payload);
    return response.data;
  },

  // -- Users ---------------------------------------------------------------
  listUsers: async ({ search, limit = 50, offset = 0 } = {}) => {
    const params = { limit, offset };
    if (search) params.search = search;
    const response = await api.get('/admin/users', { params });
    return response.data; // { users: [...], total }
  },

  getUser: async (userId) => {
    const response = await api.get(`/admin/users/${userId}`);
    return response.data;
  },

  setPlatformAdmin: async (userId, isPlatformAdmin) => {
    const response = await api.put(`/admin/users/${userId}/platform-admin`, { is_platform_admin: isPlatformAdmin });
    return response.data;
  },

  // -- Orgs (extended) -----------------------------------------------------
  setOrgStatus: async (orgId, status) => {
    const response = await api.put(`/admin/organizations/${orgId}/status`, { status });
    return response.data;
  },

  updateMember: async (orgId, memberId, payload) => {
    // payload: { role?, status? }
    const response = await api.put(`/admin/organizations/${orgId}/members/${memberId}`, payload);
    return response.data;
  },

  addMemberByEmail: async (orgId, payload) => {
    // payload: { email, role }
    const response = await api.post(`/admin/organizations/${orgId}/members/by-email`, payload);
    return response.data;
  },

  betaInvite: async (orgId, payload) => {
    // payload: { emails: string[], role, shared_password?, full_name_prefix? }
    const response = await api.post(`/admin/organizations/${orgId}/beta-invite`, payload, {
      timeout: 60000,
    });
    return response.data; // { results: [{ email, status, password?, user_id?, error? }], org_name }
  },

  // -- Billing -------------------------------------------------------------
  billingOverview: async () => {
    const response = await api.get('/admin/billing/overview');
    return response.data;
  },

  listSubscriptions: async (statusFilter) => {
    const params = statusFilter ? { status_filter: statusFilter } : {};
    const response = await api.get('/admin/subscriptions', { params });
    return response.data;
  },

  recentInvoices: async (limit = 25) => {
    const response = await api.get('/admin/billing/recent-invoices', { params: { limit } });
    return response.data;
  },

  cancelSubscription: async (orgId) => {
    const response = await api.post(`/admin/organizations/${orgId}/subscription/cancel`);
    return response.data;
  },

  refundInvoice: async (invoiceId) => {
    const response = await api.post(`/admin/invoices/${invoiceId}/refund`);
    return response.data;
  },

  // -- Audit + growth ------------------------------------------------------
  globalAudit: async ({ limit = 100, action, orgId } = {}) => {
    const params = { limit };
    if (action) params.action = action;
    if (orgId) params.org_id = orgId;
    const response = await api.get('/admin/audit', { params });
    return response.data;
  },

  growthStats: async () => {
    const response = await api.get('/admin/stats/growth');
    return response.data;
  },
};

// Templates API
export const templatesAPI = {
  getAll: async () => {
    const response = await api.get('/templates');
    return response.data; // [{ id, name, description, thumbnail_colors, available }]
  },
};

// Tenders API — appels d'offre with AI extraction + matching
export const tendersAPI = {
  // Parse without saving (paste text)
  parseText: async (text) => {
    const orgId = getOrgId();
    const response = await api.post(`/orgs/${orgId}/tenders/parse`, { text }, { timeout: 180000 });
    return response.data.result;
  },

  // Parse without saving (upload PDF/Word)
  parseFile: async (file) => {
    const orgId = getOrgId();
    const token = await getToken();
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${API_URL}/orgs/${orgId}/tenders/parse/upload`, formData, {
      headers: { Authorization: `Bearer ${token}` },
      timeout: 180000,
    });
    return response.data.result;
  },

  // Persist
  create: async (data) => {
    const orgId = getOrgId();
    const response = await api.post(`/orgs/${orgId}/tenders`, data);
    return response.data;
  },

  list: async () => {
    const orgId = getOrgId();
    const response = await api.get(`/orgs/${orgId}/tenders`);
    return response.data;
  },

  getOne: async (id) => {
    const orgId = getOrgId();
    const response = await api.get(`/orgs/${orgId}/tenders/${id}`);
    return response.data;
  },

  update: async (id, data) => {
    const orgId = getOrgId();
    const response = await api.put(`/orgs/${orgId}/tenders/${id}`, data);
    return response.data;
  },

  delete: async (id) => {
    const orgId = getOrgId();
    await api.delete(`/orgs/${orgId}/tenders/${id}`);
    return { success: true };
  },

  duplicate: async (id) => {
    const orgId = getOrgId();
    const response = await api.post(`/orgs/${orgId}/tenders/${id}/duplicate`);
    return response.data;
  },

  recompute: async (id) => {
    const orgId = getOrgId();
    const response = await api.post(`/orgs/${orgId}/tenders/${id}/recompute`);
    return response.data;
  },

  matches: async (id) => {
    const orgId = getOrgId();
    const response = await api.get(`/orgs/${orgId}/tenders/${id}/matches`);
    return response.data;
  },

  explain: async (tenderId, dossierId) => {
    const orgId = getOrgId();
    const response = await api.post(`/orgs/${orgId}/tenders/${tenderId}/matches/${dossierId}/explain`, {}, { timeout: 120000 });
    return response.data;
  },

  shortlistAdd: async (tenderId, dossierId, notes = null) => {
    const orgId = getOrgId();
    const response = await api.post(`/orgs/${orgId}/tenders/${tenderId}/shortlist/${dossierId}`, { notes });
    return response.data;
  },

  shortlistRemove: async (tenderId, dossierId) => {
    const orgId = getOrgId();
    const response = await api.delete(`/orgs/${orgId}/tenders/${tenderId}/shortlist/${dossierId}`);
    return response.data;
  },

  shortlistList: async (tenderId) => {
    const orgId = getOrgId();
    const response = await api.get(`/orgs/${orgId}/tenders/${tenderId}/shortlist`);
    return response.data;
  },

  // ── Phase 2 — analytics, reverse view, global shortlists, export ──
  analytics: async () => {
    const orgId = getOrgId();
    const response = await api.get(`/orgs/${orgId}/tenders/analytics`);
    return response.data;
  },

  reverseMatch: async (dossierId) => {
    const orgId = getOrgId();
    const response = await api.get(`/orgs/${orgId}/dossiers/${dossierId}/matching-tenders`);
    return response.data;
  },

  globalShortlists: async () => {
    const orgId = getOrgId();
    const response = await api.get(`/orgs/${orgId}/shortlists`);
    return response.data;
  },

  exportShortlistPDF: async (tenderId, title = 'AO') => {
    const orgId = getOrgId();
    const response = await api.get(`/orgs/${orgId}/tenders/${tenderId}/shortlist/export`, {
      responseType: 'blob',
      timeout: 180000,
    });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `shortlist_${(title || 'AO').replace(/[^A-Za-z0-9_-]+/g, '_')}.pdf`);
    document.body.appendChild(link);
    link.click();
    setTimeout(() => { link.remove(); window.URL.revokeObjectURL(url); }, 100);
    return { success: true };
  },
};

// Bug reports API — beta tester intake. See docs/beta-bug-reports.md.
export const bugReportsAPI = {
  // Submit a new bug. `metadata` is a JSON-serialisable object matching the
  // backend BugReportCreate model. `screenshotBlob` is an optional PNG Blob
  // (e.g. from html2canvas). Returns { id, ref, github_issue_url, ... }.
  create: async (metadata, screenshotBlob = null) => {
    const formData = new FormData();
    formData.append('metadata', JSON.stringify(metadata));
    if (screenshotBlob) {
      formData.append('screenshot', screenshotBlob, 'screenshot.png');
    }
    const response = await api.post('/bug-reports', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 30000,
    });
    return response.data;
  },

  // Platform admin only
  list: async ({ status, severity, orgId, limit } = {}) => {
    const params = {};
    if (status) params.status_filter = status;
    if (severity) params.severity = severity;
    if (orgId) params.org_id = orgId;
    if (limit) params.limit = limit;
    const response = await api.get('/bug-reports', { params });
    return response.data; // { reports: [...] }
  },

  get: async (id) => {
    const response = await api.get(`/bug-reports/${id}`);
    return response.data;
  },

  update: async (id, { status, assignee_notes } = {}) => {
    const response = await api.patch(`/bug-reports/${id}`, { status, assignee_notes });
    return response.data;
  },

  // Pull the current state of every linked GitHub issue and reconcile the
  // local rows. Use after a missed webhook delivery, or when running without
  // a webhook at all. Returns { checked, updated, deleted_on_github, errors }.
  syncFromGitHub: async () => {
    const response = await api.post('/bug-reports/sync-from-github', null, {
      timeout: 60000,
    });
    return response.data;
  },
};

// CRM client API — comptes clients > entités > équipes > fiches, et journal
// d'actions. Remplace la persistance localStorage de la bêta Cockpit/CRM.
export const crmAPI = {
  // Arbre complet de l'organisation : comptes > entités > équipes > fiches.
  // Un seul appel au chargement du CRM, plutôt qu'une requête par niveau.
  getTree: async () => {
    const orgId = getOrgId();
    const response = await api.get(`/orgs/${orgId}/crm/tree`);
    return response.data;
  },

  // ── Comptes ──
  listAccounts: async () => {
    const orgId = getOrgId();
    const response = await api.get(`/orgs/${orgId}/crm/accounts`);
    return response.data;
  },
  getAccount: async (id) => {
    const orgId = getOrgId();
    const response = await api.get(`/orgs/${orgId}/crm/accounts/${id}`);
    return response.data;
  },
  createAccount: async (data) => {
    const orgId = getOrgId();
    const response = await api.post(`/orgs/${orgId}/crm/accounts`, data);
    return response.data;
  },
  updateAccount: async (id, data) => {
    const orgId = getOrgId();
    const response = await api.put(`/orgs/${orgId}/crm/accounts/${id}`, data);
    return response.data;
  },
  deleteAccount: async (id) => {
    const orgId = getOrgId();
    const response = await api.delete(`/orgs/${orgId}/crm/accounts/${id}`);
    return response.data;
  },

  // ── Entités ──
  listEntities: async (accountId) => {
    const orgId = getOrgId();
    const response = await api.get(`/orgs/${orgId}/crm/entities`, {
      params: accountId ? { account_id: accountId } : undefined,
    });
    return response.data;
  },
  createEntity: async (data) => {
    const orgId = getOrgId();
    const response = await api.post(`/orgs/${orgId}/crm/entities`, data);
    return response.data;
  },
  updateEntity: async (id, data) => {
    const orgId = getOrgId();
    const response = await api.put(`/orgs/${orgId}/crm/entities/${id}`, data);
    return response.data;
  },
  deleteEntity: async (id) => {
    const orgId = getOrgId();
    const response = await api.delete(`/orgs/${orgId}/crm/entities/${id}`);
    return response.data;
  },

  // ── Équipes ──
  listTeams: async (entityId) => {
    const orgId = getOrgId();
    const response = await api.get(`/orgs/${orgId}/crm/teams`, {
      params: entityId ? { entity_id: entityId } : undefined,
    });
    return response.data;
  },
  createTeam: async (data) => {
    const orgId = getOrgId();
    const response = await api.post(`/orgs/${orgId}/crm/teams`, data);
    return response.data;
  },
  updateTeam: async (id, data) => {
    const orgId = getOrgId();
    const response = await api.put(`/orgs/${orgId}/crm/teams/${id}`, data);
    return response.data;
  },
  deleteTeam: async (id) => {
    const orgId = getOrgId();
    const response = await api.delete(`/orgs/${orgId}/crm/teams/${id}`);
    return response.data;
  },

  // ── Fiches clients ──
  // Les filtres sont cumulables ; account_id/entity_id sont dérivés de
  // l'équipe côté serveur — ne jamais les envoyer à la création.
  listContacts: async (filters = {}) => {
    const orgId = getOrgId();
    const response = await api.get(`/orgs/${orgId}/crm/contacts`, { params: filters });
    return response.data;
  },
  getContact: async (id) => {
    const orgId = getOrgId();
    const response = await api.get(`/orgs/${orgId}/crm/contacts/${id}`);
    return response.data;
  },
  createContact: async (data) => {
    const orgId = getOrgId();
    const response = await api.post(`/orgs/${orgId}/crm/contacts`, data);
    return response.data;
  },
  updateContact: async (id, data) => {
    const orgId = getOrgId();
    const response = await api.put(`/orgs/${orgId}/crm/contacts/${id}`, data);
    return response.data;
  },
  deleteContact: async (id) => {
    const orgId = getOrgId();
    const response = await api.delete(`/orgs/${orgId}/crm/contacts/${id}`);
    return response.data;
  },

  // ── Journal d'actions ──
  // Une action est un fait daté : elle se crée et se supprime, jamais ne se modifie.
  listActions: async (contactId) => {
    const orgId = getOrgId();
    const response = await api.get(`/orgs/${orgId}/crm/contacts/${contactId}/actions`);
    return response.data;
  },
  createAction: async (contactId, data) => {
    const orgId = getOrgId();
    const response = await api.post(`/orgs/${orgId}/crm/contacts/${contactId}/actions`, data);
    return response.data;
  },
  deleteAction: async (actionId) => {
    const orgId = getOrgId();
    const response = await api.delete(`/orgs/${orgId}/crm/actions/${actionId}`);
    return response.data;
  },
};

export default api;
